<template>
  <section class="processing-panel">
    <div class="processing-heading">
      <div>
        <h3>Prepare ceremonies for search</h3>
        <p>Admin only. Processing runs in the background and uses GPU capacity.</p>
      </div>
      <span class="admin-badge">Admin</span>
    </div>

    <div class="processing-grid">
      <div>
        <label class="field-label">University</label>
        <div class="field-readonly">{{ selectedSlug || 'No university selected' }}</div>
      </div>
      <div>
        <label class="field-label">Season</label>
        <select v-model="selectedSeasonKey" class="field-input" :disabled="!seasons.length">
          <option value="">Select season</option>
          <option v-for="season in seasons" :key="season.key" :value="season.key">
            {{ season.label }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="seasonCeremonies.length" class="ceremony-selection">
      <div class="selection-toolbar">
        <span>{{ selectedCeremonyIds.length }} selected (maximum {{ MAX_INDEX_CEREMONIES }})</span>
        <div>
          <button type="button" class="link-button" @click="selectSeason">Select up to 25</button>
          <button type="button" class="link-button" @click="selectedCeremonyIds = []">Clear</button>
        </div>
      </div>
      <label
        v-for="ceremony in seasonCeremonies"
        :key="ceremony.id"
        class="ceremony-option"
      >
        <input
          v-model="selectedCeremonyIds"
          type="checkbox"
          :value="String(ceremony.id)"
          :disabled="selectionLimitReached && !selectedCeremonyIds.includes(String(ceremony.id))"
        />
        <span>{{ formatCeremony(ceremony) }}</span>
      </label>
    </div>

    <div v-if="confirmationOpen" class="confirmation" role="alert">
      <p>
        Queue {{ selectedCeremonyIds.length }} ceremon{{ selectedCeremonyIds.length === 1 ? 'y' : 'ies' }}?
        Ready or currently processing ceremonies will be skipped safely.
      </p>
      <div class="actions">
        <button type="button" class="primary-button" :disabled="submitting" @click="submit">
          {{ submitting ? 'Queuing…' : 'Queue processing' }}
        </button>
        <button type="button" class="secondary-button" :disabled="submitting" @click="confirmationOpen = false">
          Cancel
        </button>
      </div>
    </div>
    <button
      v-else
      type="button"
      class="primary-button"
      :disabled="!selectedCeremonyIds.length"
      @click="confirmationOpen = true"
    >
      Review processing request
    </button>

    <p v-if="error" class="processing-error" role="alert">{{ error }}</p>

    <div v-if="results.length" class="results">
      <div class="results-heading">
        <h4>Processing status</h4>
        <button type="button" class="link-button" :disabled="refreshing" @click="refreshStatuses">
          {{ refreshing ? 'Refreshing…' : 'Refresh status' }}
        </button>
      </div>
      <div v-for="item in results" :key="item.ceremony_id" class="result-row">
        <span>{{ ceremonyLabel(item.ceremony_id) }}</span>
        <strong :class="`status-${item.status}`">{{ statusLabel(item) }}</strong>
      </div>
    </div>
  </section>
</template>

<script>
import { buildCeremonySeasons } from '../../../admin/ceremonySeasons.js'
import { trackAdminEvent } from '../../../admin/activity.js'
import {
  MAX_INDEX_CEREMONIES,
  getCeremonyIndexingStatus,
  startCeremonyIndexing,
} from '../../../api/ceremonyIndexing.js'

export default {
  name: 'CeremonyProcessingControls',
  props: {
    selectedSlug: {
      type: String,
      default: '',
    },
    ceremonies: {
      type: Array,
      default: () => [],
    },
    formatCeremony: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      MAX_INDEX_CEREMONIES,
      selectedSeasonKey: '',
      selectedCeremonyIds: [],
      confirmationOpen: false,
      submitting: false,
      refreshing: false,
      error: '',
      results: [],
    }
  },
  computed: {
    seasons() {
      return buildCeremonySeasons(this.ceremonies)
    },
    selectedSeason() {
      return this.seasons.find(season => season.key === this.selectedSeasonKey)
    },
    seasonCeremonies() {
      return this.selectedSeason?.ceremonies || []
    },
    selectionLimitReached() {
      return this.selectedCeremonyIds.length >= MAX_INDEX_CEREMONIES
    },
  },
  watch: {
    seasons: {
      immediate: true,
      handler(seasons) {
        if (!seasons.some(season => season.key === this.selectedSeasonKey)) {
          this.selectedSeasonKey = seasons[0]?.key || ''
        }
      },
    },
    selectedSeasonKey() {
      this.selectedCeremonyIds = []
      this.confirmationOpen = false
    },
    selectedSlug() {
      this.results = []
      this.error = ''
    },
  },
  methods: {
    selectSeason() {
      this.selectedCeremonyIds = this.seasonCeremonies
        .slice(0, MAX_INDEX_CEREMONIES)
        .map(ceremony => String(ceremony.id))
    },
    ceremonyLabel(ceremonyId) {
      const ceremony = this.ceremonies.find(row => String(row.id) === ceremonyId)
      return ceremony ? this.formatCeremony(ceremony) : ceremonyId
    },
    statusLabel(item) {
      if (item.status === 'FAILED' && item.error_code === 'DURATION_UNAVAILABLE') {
        return 'Failed: source duration unavailable'
      }
      if (item.status === 'FAILED' && item.error_code === 'START_EXECUTION_FAILED') {
        return 'Failed to start'
      }
      const labels = {
        queued: 'Queued',
        already_ready: 'Ready',
        already_processing: 'Processing',
        source_not_ready: 'Source not ready',
        not_found: 'Not found',
        error: 'Could not queue',
        NOT_STARTED: 'Not started',
        REQUESTED: 'Queued',
        PLANNED: 'Planning',
        SUBMITTED: 'Submitted',
        RUNNING: 'Processing',
        SUCCEEDED: item.ready ? 'Ready' : 'Finished without vectors',
        FAILED: 'Failed',
      }
      return labels[item.status] || item.status
    },
    async submit() {
      if (!this.selectedCeremonyIds.length || this.submitting) return
      this.submitting = true
      this.error = ''
      try {
        const response = await startCeremonyIndexing(this.selectedCeremonyIds)
        this.results = response.data?.results || []
        this.confirmationOpen = false
        trackAdminEvent('embedding_processing_requested', {
          toolKey: 'ceremony_intelligence',
          metadata: {
            universitySlug: this.selectedSlug,
            requestScope: response.data?.request_scope,
            requestedCount: this.selectedCeremonyIds.length,
            queuedCount: response.data?.counts?.queued || 0,
          },
        })
      } catch (error) {
        this.error = error?.response?.data?.error || 'Processing could not be queued.'
      } finally {
        this.submitting = false
      }
    },
    async refreshStatuses() {
      if (!this.results.length || this.refreshing) return
      this.refreshing = true
      this.error = ''
      const refreshed = await Promise.all(
        this.results.map(async item => {
          try {
            const response = await getCeremonyIndexingStatus(item.ceremony_id)
            return {
              ceremony_id: response.data.ceremony_id,
              ...(response.data.job || {}),
            }
          } catch (_) {
            return { ...item, refreshFailed: true }
          }
        })
      )
      this.results = refreshed
      if (refreshed.some(item => item.refreshFailed)) {
        this.error = 'Some processing statuses could not be refreshed.'
      }
      this.refreshing = false
    },
  },
}
</script>

<style scoped>
.processing-panel {
  margin-bottom: 24px;
  padding: 20px;
  border: 1px solid #d6dbe5;
  border-radius: .25rem;
  background: #f8fafc;
}

.processing-heading,
.selection-toolbar,
.results-heading,
.result-row,
.actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.processing-heading h3,
.results-heading h4 {
  margin: 0;
  font-weight: 650;
}

.processing-heading p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: .86rem;
}

.admin-badge {
  padding: 3px 8px;
  border-radius: 999px;
  background: #0f172a;
  color: #fff;
  font-size: .72rem;
}

.processing-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 18px;
}

.field-label {
  display: block;
  margin-bottom: 6px;
  color: #4b5563;
  font-size: .875rem;
}

.field-input,
.field-readonly {
  width: 100%;
  min-height: 42px;
  padding: 8px 10px;
  border: 1px solid #cfd7e3;
  border-radius: .25rem;
  background: #fff;
}

.ceremony-selection,
.confirmation,
.results {
  margin-top: 16px;
}

.selection-toolbar,
.results-heading {
  margin-bottom: 8px;
  color: #475569;
  font-size: .82rem;
}

.ceremony-option {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 4px;
  border-bottom: 1px solid #e2e8f0;
  font-size: .86rem;
}

.confirmation {
  padding: 14px;
  border: 1px solid #f59e0b;
  background: #fffbeb;
}

.confirmation p {
  margin: 0 0 12px;
}

.actions {
  justify-content: flex-start;
}

.primary-button,
.secondary-button,
.link-button {
  border-radius: .25rem;
  font-weight: 650;
}

.primary-button,
.secondary-button {
  min-height: 38px;
  padding: 0 14px;
}

.primary-button {
  border: 1px solid #0f172a;
  background: #0f172a;
  color: #fff;
}

.secondary-button {
  border: 1px solid #cbd5e1;
  background: #fff;
}

.link-button {
  margin-left: 10px;
  border: 0;
  background: transparent;
  color: #2563eb;
}

button:disabled {
  cursor: not-allowed;
  opacity: .5;
}

.processing-error {
  margin: 12px 0 0;
  color: #b42318;
  font-size: .86rem;
}

.result-row {
  padding: 9px 0;
  border-bottom: 1px solid #e2e8f0;
  font-size: .84rem;
}

.result-row strong {
  white-space: nowrap;
}

.status-queued,
.status-REQUESTED,
.status-PLANNED,
.status-SUBMITTED,
.status-RUNNING,
.status-already_processing {
  color: #9a6700;
}

.status-already_ready,
.status-SUCCEEDED {
  color: #087f5b;
}

.status-error,
.status-FAILED {
  color: #b42318;
}

@media (max-width: 700px) {
  .processing-grid {
    grid-template-columns: 1fr;
  }
}
</style>
